import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import { Box, Button, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'

import { api, ApiError, type GpuStatus, type PowerState } from '../api/client'
import { formatUptime, sessionCost, uptimeSeconds } from '../lib/runningCost'
import ConfirmDialog from './ConfirmDialog'

/** How often to re-check. Faster while something is changing, because that is
 *  when the user is actually watching. */
const POLL_SETTLED = 30_000
const POLL_CHANGING = 5_000

/** How often the on-screen uptime and cost are recomputed. */
const TICK_MS = 15_000

const APPEARANCE: Record<PowerState, { label: string; color: 'success' | 'warning' | 'default' }> =
  {
    online: { label: 'GPU online', color: 'success' },
    booting: { label: 'GPU booting', color: 'warning' },
    starting: { label: 'GPU starting', color: 'warning' },
    stopping: { label: 'GPU stopping', color: 'warning' },
    off: { label: 'GPU off', color: 'default' },
    unknown: { label: 'GPU unknown', color: 'default' },
  }

const CHANGING: PowerState[] = ['starting', 'booting', 'stopping']

/**
 * Always in the header. Two reasons it lives there rather than on a page:
 * whether the meter is running is worth knowing at a glance, and switching it
 * off should be one click from wherever you are -- forgetting is what costs
 * money.
 */
export default function GpuControl({ onChange }: { onChange?: (status: GpuStatus) => void }) {
  const [status, setStatus] = useState<GpuStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)

  const load = useCallback(async () => {
    try {
      const next = await api.gpuStatus()
      setStatus(next)
      onChange?.(next)
      setError(null)
    } catch (cause) {
      if (cause instanceof ApiError && cause.isUnauthenticated) return
      setError(cause instanceof Error ? cause.message : 'Could not read the GPU status.')
    }
  }, [onChange])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const interval = status && CHANGING.includes(status.state) ? POLL_CHANGING : POLL_SETTLED
    timer.current = window.setTimeout(() => void load(), interval)
    return () => window.clearTimeout(timer.current)
  }, [status, load])

  /** Which power confirmation is open. Both directions ask; see the dialogs. */
  const [asking, setAsking] = useState<'on' | 'off' | null>(null)

  /**
   * Advances the uptime and cost on screen. Re-rendering only on the status poll
   * would leave the number looking frozen, which is the opposite of a meter --
   * but a one-second tick was redrawing for nothing: the uptime is shown in
   * whole minutes, and at the current rate a single cent takes about 46 seconds
   * to accrue. Fifteen seconds is finer than anything the display can show.
   */
  const [now, setNow] = useState(() => Date.now())
  const upSeconds = uptimeSeconds(status?.running_since ?? null, now)
  useEffect(() => {
    if (upSeconds === null) return
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS)
    return () => window.clearInterval(timer)
  }, [upSeconds === null])

  async function act(action: 'on' | 'off') {
    setBusy(true)
    setError(null)
    try {
      const next = action === 'on' ? await api.gpuPowerOn() : await api.gpuPowerOff()
      setStatus(next)
      onChange?.(next)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  const cost = upSeconds === null ? null : sessionCost(upSeconds, status?.hourly_cost_eur ?? null)

  if (!status) {
    return <CircularProgress size={20} />
  }

  const look = APPEARANCE[status.state]
  // The server already filtered these to poweron/poweroff. Scaleway also
  // offers `terminate`, which destroys the instance -- it must never appear.
  const canStart = status.available_actions.includes('poweron')
  const canStop = status.available_actions.includes('poweroff')

  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {error && (
        <Typography variant="caption" color="error" sx={{ maxWidth: 260 }}>
          {error}
        </Typography>
      )}

      <Tooltip title={status.detail}>
        <Chip
          size="small"
          variant="outlined"
          color={look.color}
          label={look.label}
          icon={
            CHANGING.includes(status.state) ? (
              <Box sx={{ display: 'flex', pl: 0.75 }}>
                <CircularProgress size={12} color="inherit" />
              </Box>
            ) : undefined
          }
        />
      </Tooltip>

      {upSeconds !== null && (
        <Tooltip
          title={
            cost === null
              ? 'Since Scaleway last recorded a change to the server, which for this VM is the power-on.'
              : `About ${status.currency} ${(status.hourly_cost_eur ?? 0).toFixed(2)} per hour while it is on.`
          }
        >
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            On for {formatUptime(upSeconds)}
            {cost !== null && ` · ${status.currency} ${cost.toFixed(2)}`}
          </Typography>
        </Tooltip>
      )}

      {status.power_control_configured && canStart && (
        <Button
          size="small"
          variant="contained"
          startIcon={<PowerSettingsNewIcon />}
          onClick={() => setAsking('on')}
          disabled={busy}
        >
          Power On
        </Button>
      )}
      {status.power_control_configured && canStop && (
        <Button
          size="small"
          variant="outlined"
          color="warning"
          startIcon={<PowerSettingsNewIcon />}
          onClick={() => setAsking('off')}
          disabled={busy}
        >
          Power Off
        </Button>
      )}

      <ConfirmDialog
        open={asking === 'on'}
        title="Power on the GPU?"
        busy={busy}
        confirmLabel="Power On"
        body="This starts hourly billing, and it keeps running until someone powers it off again. Nothing switches it off on its own."
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          setAsking(null)
          void act('on')
        }}
      />

      <ConfirmDialog
        open={asking === 'off'}
        title="Power off the GPU?"
        busy={busy}
        confirmLabel="Power Off"
        // Not destructive: it is the direction that stops the meter, and the
        // server refuses it outright while a job is still running.
        body="Billing stops. Results already copied here stay downloadable, and anyone ordering a cover will have to start it again."
        onCancel={() => setAsking(null)}
        onConfirm={() => {
          setAsking(null)
          void act('off')
        }}
      />
    </Stack>
  )
}
